/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useState } from 'react';
import React from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import ReactTimeAgo from 'react-time-ago';
import { createColumnHelper } from '@tanstack/react-table';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en.json';

import { useDeleteSegmentById } from '@/api/segments/useDeleteSegment';
import { useAppSelector } from '@/app/hooks';
import Badge from '@/component-library/primitives/Badge/Badge';
import ArrowDownUpIcon from '@/component-library/primitives/Icons/ArrowDownUpIcon/ArrowDownUpIcon';
import DataFlowIcon from '@/component-library/primitives/Icons/DataFlowIcon/DataFlowIcon';
import EditPencilIcon from '@/component-library/primitives/Icons/EditPencilIcon/EditPencilIcon';
import NoteSearchIcon from '@/component-library/primitives/Icons/NoteSearchIcon/NoteSearchIcon';
import SettingsFutureIcon from '@/component-library/primitives/Icons/SettingsFutureIcon/SettingsFutureIcon';
import TrashIcon from '@/component-library/primitives/Icons/TrashIcon/TrashIcon';
import UsersIcon from '@/component-library/primitives/Icons/UsersIcon/UsersIcon';
import Modal from '@/component-library/primitives/Modal/Modal';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import { Segment } from '@/models/Segments';
import { HttpMethodsEnum, PermissionsEnum } from '@/modules/shared/enums/enums';
import usePermissionChecker from '@/modules/shared/hooks/usePermissionChecker';

import queryKeys from '../../shared/query/queryKeys';
import { selectCurrentWorkspace } from '../Workspaces/workspaceSlice';

export const useSegmentsTable = () => {
  TimeAgo.setDefaultLocale(en.locale);
  TimeAgo.addLocale(en);
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<Segment>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const deleteSegmentMutation = useDeleteSegmentById();
  const [deletingSegmentId, setDeletingSegmentId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const workspace = useAppSelector(selectCurrentWorkspace);

  const isActionAllowed = usePermissionChecker(PermissionsEnum.SEGMENTS, HttpMethodsEnum.PUT);
  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const dropdownRefs: { [rowId: string]: React.RefObject<HTMLDivElement> } = {};

  const handleDropdownClick = (event: React.MouseEvent, rowId: string) => {
    event.stopPropagation();

    if (openDropdownId === rowId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(rowId);
    }
  };

  const handleDeleteModal = (state: boolean) => {
    // @ts-ignore
    if (!document.startViewTransition) {
      setShowDeleteModal(state);
    } else {
      // @ts-ignore
      document.startViewTransition(() => {
        flushSync(() => {
          setShowDeleteModal(state);
        });
      });
    }
  };

  const handleOnEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isActionAllowed) {
      setToastInfo({ message: t('CommonErrors.actionNotAllowed'), toastType: 'error' });
      setOpen(true);
      return;
    }
    console.log('handleOnEdit');
  };

  const handleOnDelete = (e: React.MouseEvent, segmentId: string | null) => {
    e.stopPropagation();
    if (!isActionAllowed) {
      setToastInfo({ message: t('CommonErrors.actionNotAllowed'), toastType: 'error' });
      setOpen(true);
      return;
    }
    setDeletingSegmentId(segmentId);
    handleDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (deletingSegmentId) {
      deleteSegmentMutation.mutate(deletingSegmentId, {
        onSuccess: () => {
          setTimeout(() => {
            queryClient.setQueryData<Segment[]>(
              [queryKeys.segments, workspace?.id],
              (prevData) => prevData?.filter((segment) => segment.id !== deletingSegmentId) || [],
            );
            setToastInfo({ message: t('SegmentsPage.toastDeleteSuccess'), toastType: 'success' });
            setOpen(true);
          }, 1000);
        },
        onError: () => {
          // TODO: handle error here, like showing an error message
          console.log('Error deleting segment');
        },
        onSettled: () => {
          setDeletingSegmentId(null);
        },
      });
    }
    handleDeleteModal(false);
  };

  useEffect(() => {
    const dropdownRefKeys = Object.keys(dropdownRefs);

    const closeDropdownsExceptCurrent = (currentRowId: string | null) => {
      dropdownRefKeys.forEach((rowId) => {
        if (rowId === currentRowId) {
          setOpenDropdownId(null);
        }
      });
    };

    const handleOutsideClick = () => {
      closeDropdownsExceptCurrent(openDropdownId);
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [openDropdownId]);

  // Helper function to parse rule and generate criteria preview
  const getCriteriaPreview = (segment: Segment): string => {
    if (!segment.rule) {
      return 'No criteria defined';
    }

    try {
      const parsedRule = JSON.parse(segment.rule);
      const conditions = parsedRule.rule?.decisions?.[0]?.conditions;
      if (!conditions) {
        return 'No criteria defined';
      }

      const conditionArray = Object.values(conditions)[0] as any[];
      if (!Array.isArray(conditionArray) || conditionArray.length === 0) {
        return 'No criteria defined';
      }

      const criteriaParts: string[] = [];
      const riskScoreConditions: any[] = [];

      conditionArray.forEach((condition: any) => {
        if (condition.fact === 'email' && condition.operator === 'contains') {
          const domain = condition.value.replace('@', '');
          criteriaParts.push(`Email: @${domain}`);
        } else if (condition.fact === 'attribute' && condition.params) {
          const key = condition.params.key;
          if (key === 'riskScore') {
            riskScoreConditions.push(condition);
          } else if (key === 'status') {
            criteriaParts.push(`Status: ${condition.value}`);
          } else if (key === 'recommendedActions') {
            criteriaParts.push(`Action: ${condition.value}`);
          }
        }
      });

      // Handle risk score range
      if (riskScoreConditions.length >= 2) {
        const fromCondition = riskScoreConditions.find((c: any) => c.operator === 'greaterThanInclusive');
        const toCondition = riskScoreConditions.find((c: any) => c.operator === 'lessThanInclusive');
        if (fromCondition && toCondition) {
          criteriaParts.push(`Risk Score: ${fromCondition.value}-${toCondition.value}`);
        } else if (fromCondition) {
          criteriaParts.push(`Risk Score: ≥${fromCondition.value}`);
        } else if (toCondition) {
          criteriaParts.push(`Risk Score: ≤${toCondition.value}`);
        }
      } else if (riskScoreConditions.length === 1) {
        const condition = riskScoreConditions[0];
        if (condition.operator === 'greaterThanInclusive') {
          criteriaParts.push(`Risk Score: ≥${condition.value}`);
        } else if (condition.operator === 'lessThanInclusive') {
          criteriaParts.push(`Risk Score: ≤${condition.value}`);
        }
      }

      // Handle risk level (if we can infer it from risk score ranges)
      // This is a simplified approach - in reality, risk level might be stored separately
      if (riskScoreConditions.length > 0) {
        const fromValue = riskScoreConditions.find((c: any) => c.operator === 'greaterThanInclusive')?.value;
        const toValue = riskScoreConditions.find((c: any) => c.operator === 'lessThanInclusive')?.value;
        if (fromValue >= 60 || (fromValue >= 30 && toValue && toValue < 60)) {
          // Could be high or medium risk, but we'll show the range instead
        }
      }

      return criteriaParts.length > 0 ? criteriaParts.join(' • ') : 'No criteria defined';
    } catch (error) {
      console.error('Error parsing segment rule:', error);
      return 'Error parsing criteria';
    }
  };

  const columns = [
    columnHelper.accessor('name', {
      header: () => (
        <div className="flex flex-row items-center gap-1">
          <span className="text-gray-700 font-medium">{t('SegmentsPage.nameColumn')}</span>
          <span className="cursor-pointer">
            <ArrowDownUpIcon />
          </span>
        </div>
      ),
      cell: (info) => (
        <div className="h-full mt-6 block items-start min-w-[20rem]">
          <div className="flex flex-col gap-y-1">
            <div className="flex flex-row items-center gap-2">
              <span className="text-blue-600 font-semibold text-sm">{info.renderValue()}</span>
              <NoteSearchIcon />
            </div>
            <span
              className={`text-xs block truncate max-w-[25rem] ${
                info.row.original.description ? 'text-gray-600' : 'text-gray-400 italic'
              }`}
            >
              {info.row.original.description || '<No description>'}
            </span>
            <div className="flex flex-row gap-x-1 text-gray-500 text-xs truncate max-w-[30rem]">
              <span>{t('SegmentsPage.createdLabel')}</span>
              <span>
                on{' '}
                {new Date(info.row.original.createdAt as string).toLocaleDateString('en-us', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
              <span>
                at{' '}
                {new Date(info.row.original.createdAt as string).toLocaleTimeString('en-us', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span> | </span>
              <span>{t('SegmentsPage.lastEditedLabel')}</span>
              <span className="truncate">
                <ReactTimeAgo date={new Date((info.row.original.lastModifiedAt as string) ?? 0)} locale="en-US" />
              </span>
            </div>
          </div>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'criteria',
      header: () => <div className="text-gray-700 font-medium">Criteria</div>,
      cell: (info) => {
        const criteriaPreview = getCriteriaPreview(info.row.original);
        const criteriaParts = criteriaPreview.split(' • ');

        return (
          <div className="h-full mt-6 block items-start min-w-[20rem]">
            <div className="flex flex-col gap-y-1">
              {criteriaParts.map((part, index) => (
                <div key={index} className="text-gray-600 text-sm">
                  {part}
                </div>
              ))}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor('peopleCount', {
      header: () => <div className="text-gray-700 font-medium">{t('SegmentsPage.peopleCountColumn')}</div>,
      cell: (info) => (
        <div className="h-full mt-6 block items-start">
          <div className="flex flex-row items-center gap-2">
            <UsersIcon color="#6B7280" />
            <span className="text-gray-700 text-sm font-medium">{info.renderValue() ?? 0}</span>
          </div>
        </div>
      ),
    }),
  ];

  const deleteConfirmationModal = (
    <Modal
      isOpen={showDeleteModal}
      title={t('SegmentsPage.modalTitle')}
      body={t('SegmentsPage.modalBody')}
      cancelBtnTxt={t('SegmentsPage.modalCancelBtn')}
      confirmBtnTxt={t('SegmentsPage.modalConfirmBtn')}
      onCancel={() => handleDeleteModal(false)}
      onConfirm={handleConfirmDelete}
    />
  );

  return { columns, deleteConfirmationModal };
};
